import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { ExternalLink, Calendar, Building2, X } from 'lucide-react';

const ArticleModal = ({ article, isOpen, onClose, onReadFullStory }) => {
  if (!article) return null;

  const handleReadFullStory = (e) => {
    e.preventDefault();
    onReadFullStory(article.url);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-full sm:max-w-3xl w-full h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto m-0 sm:m-4 rounded-none sm:rounded-lg" 
        data-testid="article-modal" 
        aria-describedby="article-description"
      >
        {/* Mobile Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 sm:hidden z-10 bg-white rounded-full p-2 shadow-lg"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <DialogHeader>
          <DialogTitle className="font-serif text-2xl sm:text-3xl font-bold pr-10 sm:pr-8 leading-tight">
            {article.title}
          </DialogTitle>
        </DialogHeader>

        {/* Article Metadata */}
        <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600 border-b pb-3 sm:pb-4">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            <span className="font-medium">{article.source.name}</span>
          </div>
          {article.pub_date && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{new Date(article.pub_date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}</span>
            </div>
          )}
        </div>

        {/* Article Image */}
        {article.image_url && (
          <div className="rounded-none sm:rounded-lg overflow-hidden -mx-6 sm:mx-0">
            <img
              src={article.image_url}
              alt={article.title}
              className="w-full h-48 sm:h-64 object-cover"
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&q=80';
              }}
            />
          </div>
        )}

        {/* Article Description */}
        <div className="prose prose-sm sm:prose-lg max-w-none" id="article-description">
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap text-sm sm:text-base" data-testid="article-description">
            {article.description || 'No summary available for this article.'}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6 pt-4 border-t">
          <Button 
            variant="outline" 
            onClick={onClose} 
            data-testid="modal-close-button"
            className="w-full sm:w-auto order-2 sm:order-1"
          >
            Close
          </Button>
          <Button 
            onClick={handleReadFullStory} 
            data-testid="read-full-story-button"
            className="w-full sm:w-auto order-1 sm:order-2"
          >
            <span className="hidden sm:inline">Read Full Story at {article.source.name}</span>
            <span className="sm:hidden">Read Full Article</span>
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ArticleModal;
